export const PREDEFINED_PROJECT_STATUSES = [
  "Completed",
  "Overdue",
  "Attention",
  "In Progress",
  "To be invoiced",
  "Invoiced",
  "Paid",
];

export const PREDEFINED_PROJECT_MANAGERS = [
  "Aseel",
  "Omar",
  "Muhab",
  "Sadegh",
  "Mustafa",
  "Zak",
];

export const PREDEFINED_PAYMENT_STATUSES = ["Paid", "Pending"];

export const PREDEFINED_BILLING_ENTITIES = ["UK", "UAE", "Unassigned"];

export const PREDEFINED_TASK_TYPES = [
  "DSS",
  "BC",
  "SR",
  "PS",
  "VS",
  "IMEQ",
  "BEQ",
  "FL",
  "SS",
  "EQ",
  "LC",
  "MA",
  "RPS",
  "SV",
  "VES",
];

export const PROJECT_FILTER_KEYS = [
  "date",
  "invoicedDate",
  "paidDate",
  "client",
  "billingEntity",
  "projectManager",
  "projectStatus",
  "paymentStatus",
  "taskType",
  "country",
  "poc",
];

export const createDefaultProjectFilters = () => ({
  date: [],
  invoicedDate: [],
  paidDate: [],
  client: [],
  billingEntity: [],
  projectManager: [],
  projectStatus: [],
  paymentStatus: [],
  taskType: [],
  country: [],
  poc: [],
});

export const createEmptyProjectFilterOptions = () => ({
  date: [],
  invoicedDate: [],
  paidDate: [],
  client: [],
  billingEntity: [],
  projectManager: [],
  projectStatus: [],
  paymentStatus: [],
  taskType: [],
  country: [],
  poc: [],
});

const sortStrings = (values = []) =>
  [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

const mergeWithPredefined = (predefined = [], dynamicValues = [], selectedValues = []) => {
  const seen = new Set();
  const merged = [];

  [...predefined, ...sortStrings(dynamicValues), ...selectedValues].forEach((value) => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    merged.push(value);
  });

  return merged;
};

const incrementCount = (counts, value) => {
  if (!value) return;
  counts[value] = (counts[value] || 0) + 1;
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeString = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "number") return new Date(value);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseProjectRef = (projectRef) => {
  const match = normalizeString(projectRef).match(/^P?(\d+)-(\d{4})$/i);
  if (!match) return null;
  return {
    number: parseInt(match[1], 10),
    year: parseInt(match[2], 10),
  };
};

const getTaskValues = (project, field) => {
  const values = new Set();
  (project?.tasks || []).forEach((task) => {
    const value = normalizeString(task?.[field]);
    if (value) values.add(value);
  });
  return [...values];
};

const projectMatchesSelectedTasks = (project, selectedTaskTypes = []) => {
  if (!selectedTaskTypes.length) return true;
  const taskTypes = new Set(getTaskValues(project, "task"));
  return selectedTaskTypes.some((value) => taskTypes.has(value));
};

const projectMatchesSelectedCountries = (project, selectedCountries = []) => {
  if (!selectedCountries.length) return true;
  const countries = new Set(getTaskValues(project, "country"));
  return selectedCountries.some((value) => countries.has(value));
};

const projectMatchesDateRangeValue = (value, dateFilters = []) => {
  const dateFilter = dateFilters.find(
    (value) => typeof value === "string" && value.startsWith("range:")
  );

  if (!dateFilter) return true;

  const [, startISO, endISO] = dateFilter.split(":");
  const startMs = Date.parse(startISO);
  const endMs = Date.parse(endISO);
  const normalizedDate = normalizeDateValue(value);

  if (Number.isNaN(startMs) || Number.isNaN(endMs) || !normalizedDate) {
    return false;
  }

  const dateMs = normalizedDate.getTime();
  return dateMs >= startMs && dateMs <= endMs + (24 * 60 * 60 * 1000 - 1);
};

const matchesFieldSelection = (projectValue, selectedValues = []) => {
  if (!selectedValues.length) return true;
  return selectedValues.includes(normalizeString(projectValue));
};

export const hasLocalOnlyProjectFilters = (filters = {}) =>
  ["taskType", "country", "invoicedDate", "paidDate"].some(
    (key) => Array.isArray(filters?.[key]) && filters[key].length > 0
  );

export const hasActiveProjectFilters = (filters = {}) =>
  Object.values(filters).some((values) => Array.isArray(values) && values.length > 0);

export const matchesProjectFilters = (project, filters = {}) => {
  const safeFilters = { ...createDefaultProjectFilters(), ...filters };

  return (
    projectMatchesDateRangeValue(project?.createdAt, safeFilters.date) &&
    projectMatchesDateRangeValue(project?.dateForInvoice, safeFilters.invoicedDate) &&
    projectMatchesDateRangeValue(project?.paidOn, safeFilters.paidDate) &&
    matchesFieldSelection(project?.client, safeFilters.client) &&
    matchesFieldSelection(project?.company || "Unassigned", safeFilters.billingEntity) &&
    matchesFieldSelection(project?.projectManager, safeFilters.projectManager) &&
    matchesFieldSelection(project?.projectStatus, safeFilters.projectStatus) &&
    matchesFieldSelection(project?.paymentStatus, safeFilters.paymentStatus) &&
    projectMatchesSelectedTasks(project, safeFilters.taskType) &&
    projectMatchesSelectedCountries(project, safeFilters.country) &&
    matchesFieldSelection(project?.poc, safeFilters.poc)
  );
};

export const matchesProjectSearch = (project, search = "") => {
  const term = normalizeString(search).toLowerCase();
  if (!term) return true;

  const searchableProjectFields = [
    project?.projectRef,
    project?.client,
    project?.company,
    project?.projectManager,
    project?.projectStatus,
    project?.paymentStatus,
    project?.poc,
  ];

  const searchableTaskFields = (project?.tasks || []).flatMap((task) => [
    task?.subject,
    task?.country,
    task?.task,
    task?.taskStatus,
    task?.orderAccepted,
    task?.analyst1,
    task?.analyst2,
  ]);

  return [...searchableProjectFields, ...searchableTaskFields]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(term));
};

export const filterProjects = (projects = [], filters = {}, search = "") =>
  projects.filter(
    (project) =>
      matchesProjectFilters(project, filters) && matchesProjectSearch(project, search)
  );

export const sortProjects = (
  projects = [],
  sortColumn = "createdAt",
  sortOrder = "desc"
) => {
  const direction = sortOrder === "asc" ? 1 : -1;

  return [...projects].sort((a, b) => {
    if (sortColumn === "projectRef") {
      const aRef = parseProjectRef(a?.projectRef);
      const bRef = parseProjectRef(b?.projectRef);
      if (aRef && bRef) {
        if (aRef.year !== bRef.year) return (aRef.year - bRef.year) * direction;
        if (aRef.number !== bRef.number) {
          return (aRef.number - bRef.number) * direction;
        }
      }
      return normalizeString(a?.projectRef).localeCompare(
        normalizeString(b?.projectRef),
        undefined,
        { sensitivity: "base" }
      ) * direction;
    }

    if (sortColumn === "createdAt") {
      const aTime = normalizeDateValue(a?.createdAt)?.getTime() || 0;
      const bTime = normalizeDateValue(b?.createdAt)?.getTime() || 0;
      return (aTime - bTime) * direction;
    }

    if (sortColumn === "income" || sortColumn === "amountIncVat" || sortColumn === "cost") {
      return (normalizeNumber(a?.[sortColumn]) - normalizeNumber(b?.[sortColumn])) * direction;
    }

    return normalizeString(a?.[sortColumn]).localeCompare(
      normalizeString(b?.[sortColumn]),
      undefined,
      { sensitivity: "base" }
    ) * direction;
  });
};

const countFacetValues = (projects, facetKey) => {
  const counts = {};

  projects.forEach((project) => {
    if (facetKey === "client") {
      incrementCount(counts, normalizeString(project?.client));
      return;
    }

    if (facetKey === "billingEntity") {
      incrementCount(counts, normalizeString(project?.company || "Unassigned"));
      return;
    }

    if (facetKey === "projectManager") {
      incrementCount(counts, normalizeString(project?.projectManager));
      return;
    }

    if (facetKey === "projectStatus") {
      incrementCount(counts, normalizeString(project?.projectStatus));
      return;
    }

    if (facetKey === "paymentStatus") {
      incrementCount(counts, normalizeString(project?.paymentStatus));
      return;
    }

    if (facetKey === "taskType") {
      getTaskValues(project, "task").forEach((value) => incrementCount(counts, value));
      return;
    }

    if (facetKey === "country") {
      getTaskValues(project, "country").forEach((value) => incrementCount(counts, value));
      return;
    }

    if (facetKey === "poc") {
      incrementCount(counts, normalizeString(project?.poc));
    }
  });

  return counts;
};

export const buildProjectFilterCounts = (projects = [], filters = {}, search = "") => {
  const counts = {
    date: {},
    invoicedDate: {},
    paidDate: {},
    client: {},
    billingEntity: {},
    projectManager: {},
    projectStatus: {},
    paymentStatus: {},
    taskType: {},
    country: {},
    poc: {},
  };

  PROJECT_FILTER_KEYS.forEach((facetKey) => {
    if (facetKey === "date" || facetKey === "invoicedDate" || facetKey === "paidDate") {
      return;
    }

    const filtersWithoutFacet = {
      ...createDefaultProjectFilters(),
      ...filters,
      [facetKey]: [],
    };

    const matchingProjects = projects.filter(
      (project) =>
        matchesProjectFilters(project, filtersWithoutFacet) &&
        matchesProjectSearch(project, search)
    );

    counts[facetKey] = countFacetValues(matchingProjects, facetKey);
  });

  return counts;
};

export const buildProjectFilterOptionsFromCounts = (
  counts = {},
  filters = {}
) => ({
  date: [],
  invoicedDate: [],
  paidDate: [],
  client: mergeWithPredefined(
    [],
    Object.keys(counts.client || {}),
    filters.client || []
  ),
  billingEntity: mergeWithPredefined(
    PREDEFINED_BILLING_ENTITIES,
    Object.keys(counts.billingEntity || {}),
    filters.billingEntity || []
  ),
  projectManager: mergeWithPredefined(
    PREDEFINED_PROJECT_MANAGERS,
    Object.keys(counts.projectManager || {}),
    filters.projectManager || []
  ),
  projectStatus: mergeWithPredefined(
    PREDEFINED_PROJECT_STATUSES,
    Object.keys(counts.projectStatus || {}),
    filters.projectStatus || []
  ),
  paymentStatus: mergeWithPredefined(
    PREDEFINED_PAYMENT_STATUSES,
    Object.keys(counts.paymentStatus || {}),
    filters.paymentStatus || []
  ),
  taskType: mergeWithPredefined(
    PREDEFINED_TASK_TYPES,
    Object.keys(counts.taskType || {}),
    filters.taskType || []
  ),
  country: mergeWithPredefined(
    [],
    Object.keys(counts.country || {}),
    filters.country || []
  ),
  poc: mergeWithPredefined([], Object.keys(counts.poc || {}), filters.poc || []),
});

export const buildProjectFilterMetadata = (projects = [], filters = {}, search = "") => {
  const counts = buildProjectFilterCounts(projects, filters, search);
  const options = buildProjectFilterOptionsFromCounts(counts, filters);
  return { counts, options };
};
